import { useQuery } from 'react-query'

import queryClient from '../../queryClient'
import { request } from '../../sharedLegacy/utils/api'
import {
  type APIResponse,
  HTTPMethod,
} from '../../sharedLegacy/utils/api.types'

import {
  getAudienceToken,
  getIsClientFastForwarding,
  getIsClientSecondCam,
  getLiveId,
  getPresenterToken,
  getRole,
} from './roomInfo'

type WebinarConnectInfoDTO = {
  webrtc_identity: string
  webrtc_room: string
  webrtc_authenticator: string
  webrtc_data_authenticator: string
  webrtc_data_key: string
  webrtc_role: string
  webrtc_provider: string
  webrtc_room_url: string

  firebase_api_key: string
  firebase_auth_domain: string
  firebase_database_url: string
  firebase_storage_bucket: string

  site_name: string
  domain: string
  http_url: string
  https_url: string
  site_url: string

  streaming_method: string
  webinar_quality: '720p' | '1080p'
  token: string
  locale: string

  live_speaker_id: number
  presenter_token?: string // only available for hosts

  custom_logo_p: boolean
  custom_logo_url: string

  name?: string
  draft_p?: boolean
  live_status?: string
  broadcasting_p?: boolean
  description_html?: string
  speakers?: Array<{
    live_speaker_id: number
    name: string
    email: string
    title: string
    company: string
    has_avatar_p: boolean
    hidden_p: boolean
    avatar_url: string
    connected_p: boolean
    display_as_connected_p: boolean
    connected_role: string
    sortkey: string
  }>
  connect_name?: string
  connect_avatar_url?: string
  connect_presenter_link?: string
  connection_type?: 'rtmp' | 'whip' | string
  connection_details_url?: string
  connection_details_stream_key?: string
}

const WEBINAR_CONNECT_QUERY_KEY = 'post_live_webinar_connect'

type WebinarConnectParams = {
  live_id: number
  bust_ghosts_p: number
  prejoin_p: number
  presenter_token?: string
  role?: string
}

const webinarConnectInfoQuery = {
  queryKey: [WEBINAR_CONNECT_QUERY_KEY],
  queryFn: async () => {
    const liveId = getLiveId()
    const role = getRole()
    const isFastForwarding = getIsClientFastForwarding()
    const isClientSecondCam = getIsClientSecondCam()
    const presenterToken = getPresenterToken()

    const data: WebinarConnectParams = {
      live_id: liveId,
      role,
      bust_ghosts_p: isFastForwarding && !isClientSecondCam ? 1 : 0,
      prejoin_p: isFastForwarding ? 0 : 1,
    }

    if (role !== 'producer') {
      data.presenter_token = presenterToken
    }
    // TODO: replace request
    const webinarRequest = await request<
      WebinarConnectParams,
      APIResponse<WebinarConnectInfoDTO>
    >({
      url: '/api/2/live/webinar/connect',
      method: HTTPMethod.POST,
      data,
    })

    return {
      ...webinarRequest.data,
      live_speaker_id: webinarRequest.data.live_speaker_id || 0,
    }
  },
  enabled: getRole() !== 'audience',
  cacheTime: Infinity,
  staleTime: Infinity,
}

const getWebinarConnectInfo = () => {
  return queryClient.fetchQuery<WebinarConnectInfoDTO>(webinarConnectInfoQuery)
}

const useConnectInfoQuery = () => {
  return useQuery(webinarConnectInfoQuery)
}

const isExternalFeedConnectionType = (connectionType?: string): boolean =>
  connectionType === 'rtmp' || connectionType === 'whip'

const useIsExternalFeedConnection = (): boolean => {
  const { data: connectInfo } = useConnectInfoQuery()
  return isExternalFeedConnectionType(connectInfo?.connection_type)
}

const getLiveAuthToken = async () => {
  try {
    const webinarConnectionInfo = await getWebinarConnectInfo()
    return webinarConnectionInfo.token
  } catch (e) {
    return undefined
  }
}

const getLiveToken = async () =>
  (await (getRole() === 'audience'
    ? getAudienceToken()!
    : getLiveAuthToken())) as string

export {
  getWebinarConnectInfo,
  useConnectInfoQuery,
  useIsExternalFeedConnection,
  isExternalFeedConnectionType,
  getLiveToken,
  type WebinarConnectParams,
}
